import React from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth, hasRole } from '../contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { PageHeader } from '../components/PageHeader';
import { UserCog, ArrowRight, Shield, ShieldCheck, CheckCircle2 } from 'lucide-react';

const TABS = [
  { to: '/esd/settings/users', label: 'Users', icon: UserCog, roles: ['super_admin', 'admin'] },
  { to: '/esd/settings/roles', label: 'Roles & Permissions', icon: ShieldCheck, roles: ['super_admin'] },
  { to: '/esd/settings/approvals', label: 'Approval Workflow', icon: CheckCircle2, roles: ['super_admin'] },
];

const CARDS = [
  { to: '/esd/settings/users', title: 'User Management', description: 'Give ERP users access to ESD and assign their ESD role.', icon: UserCog, roles: ['super_admin', 'admin'], accent: 'bg-blue-50 text-[hsl(217,91%,24%)]' },
  { to: '/esd/settings/roles', title: 'Roles & Permissions', description: 'Create custom roles and configure granular access for every module and action.', icon: ShieldCheck, roles: ['super_admin'], accent: 'bg-violet-50 text-violet-700' },
  { to: '/esd/settings/approvals', title: 'Approval Workflow', description: 'Designate who can approve equipment requests, inspections, mobilizations, and maintenance.', icon: CheckCircle2, roles: ['super_admin'], accent: 'bg-emerald-50 text-emerald-700' },
];

const tabCls = ({ isActive }) =>
  `inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
    isActive ? 'border-[hsl(217,91%,24%)] text-[hsl(217,91%,24%)]' : 'border-transparent text-slate-500 hover:text-slate-900'
  }`;

const SettingsLanding = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {CARDS.filter((c) => hasRole(user, ...c.roles)).map(({ to, title, description, icon: Icon, accent }) => (
        <button key={to} onClick={() => navigate(to)} className="text-left group" data-testid={`esd-settings-card-${to.split('/').pop()}`}>
          <Card className="h-full transition-shadow hover:shadow-lg hover:border-[hsl(217,91%,24%)]">
            <CardContent className="p-6 space-y-3">
              <div className={`h-12 w-12 rounded-xl grid place-items-center ${accent}`}><Icon className="h-6 w-6" /></div>
              <div>
                <h3 className="font-semibold text-lg text-slate-900">{title}</h3>
                <p className="text-sm text-slate-500 mt-1">{description}</p>
              </div>
              <div className="inline-flex items-center text-sm font-medium text-[hsl(217,91%,24%)] group-hover:gap-2 transition-all">Open <ArrowRight className="h-4 w-4 ml-1" /></div>
            </CardContent>
          </Card>
        </button>
      ))}
    </div>
  );
};

const Settings = () => {
  const { user } = useAuth();
  const location = useLocation();
  const isLanding = /^\/esd\/settings\/?$/.test(location.pathname);
  return (
    <div className="space-y-4">
      <PageHeader title="Settings" description="ESD administration: users, roles & permissions, and approval workflow." />
      <div className="flex flex-wrap items-center gap-1 border-b border-slate-200 overflow-x-auto">
        <NavLink to="/esd/settings" end className={tabCls} data-testid="esd-settings-tab-overview"><Shield className="h-4 w-4" /> Overview</NavLink>
        {TABS.filter((t) => hasRole(user, ...t.roles)).map(({ to, label, icon: Icon }) => (
          <NavLink key={to} to={to} className={tabCls} data-testid={`esd-settings-tab-${to.split('/').pop()}`}><Icon className="h-4 w-4" /> {label}</NavLink>
        ))}
      </div>
      {isLanding ? <SettingsLanding /> : <Outlet />}
    </div>
  );
};

export default Settings;
